<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import CustomLink from "~/components/CustomLink.vue";
import Button from "~/components/atoms/Button.vue";
import TextInput from "~/components/ui/TextInput.vue";
import SelectInput from "~/components/ui/SelectInput.vue";
import CountryCodeSelect from "~/components/ui/CountryCodeSelect.vue";
import { countries } from "~/utils/countries";
import AddressSelector from "~/components/ui/AddressSelector.vue";
import ShippingStatusBadge from "~/components/ui/ShippingStatusBadge.vue";
import ShippingBreakdownModal from "~/components/ui/ShippingBreakdownModal.vue";
import CartItemFraming from "~/components/internal/CartItemFraming.vue";
import type { CartFramingSelection } from "~/components/internal/CartItemFraming.vue";
import { needsMountOrSideDepth, type CartFramingItem, type CartFramingRecord } from "~/utils/framingOrderPayload";
import FrameVisualizer from "~/components/internal/FrameVisualizer.vue";
import { useMediaQuery } from "@vueuse/core";
import { useCart } from "~/composables/useCart";
import { useAuth } from "~/composables/useAuth";
import { usePayment } from "~/composables/usePayment";
import { useShippingAvailability } from "~/composables/useShippingAvailability";
import { useMeasurementUnit } from "~/composables/useMeasurementUnit";
import { State, City } from "country-state-city";
import { getArtworkImageSrc, getArtworkSetImageSrc } from "~/utils";
import {
  calculateShippingCost,
  parseWeightKg,
  parseArtworkDimensions,
  validatePincodeWithState,
} from "~/utils/shippingCalculator";

const { isCartOpen, closeCart, cartItems, cartTotal, removeFromCart, isRemoving, refetchCart } = useCart();
const { isChecking: isCheckingAvailability, check: checkAvailability } = useAvailabilityCheck();
const cartToast = useToast();
const { processPayment } = usePayment();
const { profile, user } = useAuth();
const { formatDimensions } = useMeasurementUnit();

const config = useRuntimeConfig();
const isShippingBreakdownOpen = ref(false);
const canShowShippingBreakdown = computed(() => {
  return Boolean(
    import.meta.dev ||
    config.public.showShippingBreakdown ||
    config.public.nodeEnv === "development" ||
    config.public.appEnv === "staging" ||
    config.public.appEnv === "developement" ||
    config.public.appEnv === "development"
  );
});

// Derive image URLs
const getImageUrl = (item: any) => {
  if (!item.image) return null;
  if (item.type === 'set') return getArtworkSetImageSrc(item.image, 360);
  return getArtworkImageSrc(item.image, 360);
};

const handleRemove = (cartItemId: string) => {
  removeFromCart(cartItemId);
};

const handleContinueBrowsing = () => {
  closeCart();
  navigateTo('/artworks');
};

const isReserving = ref(false);
const isCheckoutModalOpen = ref(false);
const errors = ref<Record<string, string>>({});

const orderForm = ref({
  name: "",
  email: "",
  phone: "",
  pan: "",
  address: {
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "India",
  },
  hasGst: false,
  gstName: "",
  gstNumber: "",
  countryCode: "+91",
  countryIso: "IN",
  delivery_instructions: "",
});

// --- SHIPPING AVAILABILITY ---
const {
  isChecking: isCheckingShipping,
  isAvailable: isShippingAvailable,
  shippingType,
  statusMessage: shippingStatusMessage,
  checkShipping,
  resetShipping,
} = useShippingAvailability();

const destinationStateName = computed(() => {
  if (!orderForm.value.address.state) return '';
  return State.getStateByCodeAndCountry(orderForm.value.address.state, 'IN')?.name || orderForm.value.address.state;
});

const isDelhiLoc = computed(() => {
  return Boolean(
    orderForm.value.address.zip?.startsWith('11') ||
    destinationStateName.value?.toLowerCase().includes('delhi')
  );
});

// --- PER-ITEM FRAMING ---
// A cart can hold artworks and sets that want different things, so framing is asked once per cart
// item rather than once for the order. Each CartItemFraming child owns its own controls, preview
// and pricing and reports the finished answer back here, keyed by cart item.
const framingSelections = ref<Record<string, CartFramingSelection>>({});

const onFramingSelection = (selection: CartFramingSelection) => {
  framingSelections.value = { ...framingSelections.value, [selection.cartItemId]: selection };
};

// Each CartItemFraming owns its own framing dialog, Teleported to <body> — outside this checkout
// modal's own DOM subtree. Nuxt UI's outside-click dismiss would otherwise read a click on that
// dialog's backdrop as "outside the modal" and close the checkout modal underneath it. Tracked by
// cart item so one item's dialog closing doesn't affect another's.
const framingDialogOpenMap = ref<Record<string, boolean>>({});

const onFramingDialogOpenChange = (cartItemId: string, open: boolean) => {
  framingDialogOpenMap.value = { ...framingDialogOpenMap.value, [cartItemId]: open };
};

// Read through the cart, same as activeFramingSelections above, so a stale "open" entry left
// behind by an item that has since been removed can never keep the checkout modal permanently
// non-dismissible.
const anyFramingDialogOpen = computed(() =>
  cartItems.value.some((item) => framingDialogOpenMap.value[item.cartItemId])
);

// Read through the cart rather than over the stored map, so an answer left behind by an item that
// has since been removed can never be charged for.
const activeFramingSelections = computed(() =>
  cartItems.value
    .map((item) => framingSelections.value[item.cartItemId])
    .filter((selection): selection is CartFramingSelection => Boolean(selection))
);

const framingTotalCost = computed(() =>
  activeFramingSelections.value.reduce((sum, selection) => sum + (selection.cost || 0), 0)
);

// In cart order, so the error names the first item the buyer still has to answer for.
const unansweredFramingItems = computed(() =>
  cartItems.value.filter((item) => !framingSelections.value[item.cartItemId]?.isComplete)
);

// Distinguishes the two ways an item's framing can be incomplete, so the message actually matches
// what's missing rather than always saying "choose an option" when a choice was already made.
const itemFramingError = (cartItemId: string): string => {
  const selection = framingSelections.value[cartItemId];
  if (selection?.isComplete !== false) return "";
  if (selection.choice && needsMountOrSideDepth(selection.specs)) {
    return "Please select a mount, a side depth, or both";
  }
  return "Please choose an option for this item";
};

// From lg up every configured item's preview is listed together in the dialog's own left column,
// the way the artwork and set checkouts park their single preview. Below lg the dialog is one
// column and each item draws its own preview inline, beside the controls it belongs to.
const isPreviewBesideForm = useMediaQuery("(min-width: 1024px)");

const framePreviews = computed(() =>
  activeFramingSelections.value
    .filter((selection) => Boolean(selection.preview))
    .map((selection) => ({
      cartItemId: selection.cartItemId,
      label: selection.preview!.label,
      props: selection.preview!.props,
    }))
);

// The dialog only needs the extra width while it is showing two columns — a preview column beside
// the form — so it widens as soon as a build is being configured and otherwise keeps Nuxt UI's
// default, the same rule the artwork and set checkouts use.
const hasFramingBuild = computed(() =>
  activeFramingSelections.value.some((selection) => Boolean(selection.mode))
);

// One line per item for the receipt, which takes framing as a single string.
const framingSummary = computed(() =>
  activeFramingSelections.value
    .filter((selection) => selection.requiresChoice)
    .map((selection) => `${selection.itemName}: ${selection.choice}`)
    .join(" | ")
);

// What actually gets stored on the order for framing — one entry per cart item, each carrying the
// same clean record the standalone artwork/set checkouts store (see ~/utils/framingOrderPayload).
// Walks `cartItems` rather than `framingSelections` directly so an item removed from the cart can
// never leave a stale answer behind in the stored payload.
const framingOrderPayload = computed<CartFramingRecord>(() => ({
  scope: "cart",
  totalCost: framingTotalCost.value,
  items: cartItems.value.map((item): CartFramingItem => {
    const record = framingSelections.value[item.cartItemId]?.framingRecord ?? null;
    if (item.type === "set") {
      return { cartItemId: item.cartItemId, itemType: "set", setId: item.id, setName: item.name, framing: record as any };
    }
    return { cartItemId: item.cartItemId, itemType: "artwork", artworkId: item.id, artworkName: item.name, framing: record as any };
  }),
}));

// Calculate packaging and shipping for each package in the cart (artworks and set pieces)
const calculatedCartArtworksShipping = computed(() => {
  if (!cartItems.value || cartItems.value.length === 0) return [];

  const packages: Array<{ artwork: any; shipping: ReturnType<typeof calculateShippingCost> }> = [];

  for (const item of cartItems.value) {
    // A piece only ships in a deeper box if it already has a frame, or if this item's own framing
    // choice actually reaches it — and then at the depth that choice implies, the frame's or the
    // stretcher bars'.
    const framing = framingSelections.value[item.cartItemId] ?? null;

    if (item.type === 'artwork') {
      const { widthInches, heightInches, depthInches } = parseArtworkDimensions(item);
      if (widthInches && heightInches) {
        const isBuilt = Boolean(framing?.mode && framing.mode !== 'none' && framing.mode !== 'stretch');
        const frameDepth = isBuilt && typeof framing?.frameDepthInches === 'number' ? framing.frameDepthInches : 0;
        const shipping = calculateShippingCost(
          {
            artworkWidth: widthInches,
            artworkHeight: heightInches,
            artworkDepth: depthInches || 0,
            hasFrame: isBuilt,
            selectedFrameDepth: frameDepth,
            declaredValueInr: item.price || 0,
            actualWeightKg: parseWeightKg(item.weight),
          },
          {
            destinationState: destinationStateName.value,
            isDelhi: isDelhiLoc.value,
          }
        );
        packages.push({ artwork: item, shipping });
      }
    } else if (item.type === 'set') {
      const setArts = item.setArtworks || [];
      if (setArts.length > 0) {
        for (const setArt of setArts) {
          const { widthInches, heightInches, depthInches } = parseArtworkDimensions(setArt);
          if (widthInches && heightInches) {
            const isBuilt = Boolean(framing?.mode && framing.mode !== 'none' && framing.mode !== 'stretch') && framing!.framedArtworkIds.includes(setArt.id);
            const frameDepth = isBuilt && typeof framing?.frameDepthInches === 'number' ? framing.frameDepthInches : 0;
            const shipping = calculateShippingCost(
              {
                artworkWidth: widthInches,
                artworkHeight: heightInches,
                artworkDepth: depthInches || 0,
                hasFrame: isBuilt,
                selectedFrameDepth: frameDepth,
                declaredValueInr: setArt.listing_price || (item.price / setArts.length) || 0,
                actualWeightKg: parseWeightKg(setArt.weight),
              },
              {
                destinationState: destinationStateName.value,
                isDelhi: isDelhiLoc.value,
              }
            );
            packages.push({
              artwork: {
                ...setArt,
                name: `${item.name} (${setArt.name || 'Piece'})`,
              },
              shipping,
            });
          }
        }
      }
    }
  }

  return packages;
});

// Sum up shipping across all packages in the cart
const calculatedCartShipping = computed(() => {
  const items = calculatedCartArtworksShipping.value;
  if (!items || items.length === 0) return null;

  const packageCount = items.length;
  const totalShippingCost = Number(items.reduce((sum, i) => sum + i.shipping.totalShippingCost, 0).toFixed(2));
  const totalBillableWeight = Number(items.reduce((sum, i) => sum + i.shipping.billableWeightKg, 0).toFixed(2));
  const totalActualWeight = Number(items.reduce((sum, i) => sum + i.shipping.actualWeightKg, 0).toFixed(2));
  const totalVolumetricWeight = Number(items.reduce((sum, i) => sum + i.shipping.volumetricWeightKg, 0).toFixed(2));

  const breakdown = {
    baseFreight: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.baseFreight, 0).toFixed(2)),
    docketFee: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.docketFee, 0).toFixed(2)),
    fovFee: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.fovFee, 0).toFixed(2)),
    osOwFee: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.osOwFee, 0).toFixed(2)),
    eccFee: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.eccFee, 0).toFixed(2)),
    specialHandlingFee: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.specialHandlingFee, 0).toFixed(2)),
    remoteAreaSurcharge: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.remoteAreaSurcharge, 0).toFixed(2)),
    vchcFee: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.vchcFee, 0).toFixed(2)),
    subtotal: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.subtotal, 0).toFixed(2)),
    fuelSurcharge: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.fuelSurcharge, 0).toFixed(2)),
    caf: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.caf, 0).toFixed(2)),
    idc: Number(items.reduce((sum, i) => sum + i.shipping.breakdown.idc, 0).toFixed(2)),
  };

  return {
    items,
    packageCount,
    totalShippingCost,
    totalBillableWeight,
    totalActualWeight,
    totalVolumetricWeight,
    breakdown,
  };
});

// Online payment is India only: any other phone country code shows the International Orders note
// and keeps Pay disabled.
const isInternationalPhone = computed(() => orderForm.value.countryIso !== "IN");

const onPhoneCountryChange = (iso: string) => {
  const selected = countries.find((c) => c.iso === iso);
  orderForm.value.countryIso = iso;
  if (selected) orderForm.value.countryCode = selected.dialCode;
};

const totalShippingCost = computed(() => {
  if (isShippingAvailable.value === false || !!pincodeMismatchError.value) {
    return 0;
  }
  return calculatedCartShipping.value?.totalShippingCost || 0;
});

// Framing and shipping are both taxable alongside the artworks, which is how the order endpoint
// works the amount out — the two have to agree or the buyer is charged something they never saw.
const taxableAmount = computed(() => (cartTotal.value || 0) + framingTotalCost.value + totalShippingCost.value);

const gstAmount = computed(() => {
  return Number((taxableAmount.value * 0.05).toFixed(2));
});

const totalOrderAmount = computed(() => {
  return Number((taxableAmount.value + gstAmount.value).toFixed(2));
});

const pincodeMismatch = computed(() => {
  return validatePincodeWithState(orderForm.value.address.zip, destinationStateName.value);
});

const pincodeMismatchError = computed(() => {
  return pincodeMismatch.value.isValid ? '' : pincodeMismatch.value.error;
});

watch(
  [() => orderForm.value.address.zip, () => destinationStateName.value],
  ([newZip, newState]) => {
    if (newZip && newZip.length === 6) {
      const check = validatePincodeWithState(newZip, newState);
      if (!check.isValid) {
        errors.value.zip = check.error || 'Pincode does not match selected state';
        resetShipping();
        return;
      } else {
        if (errors.value.zip && errors.value.zip.includes('belongs to')) {
          delete errors.value.zip;
        }
      }
      checkShipping(newZip);
    } else {
      resetShipping();
    }
  },
  { immediate: true }
);

const proceedToCheckout = async () => {
  // Only start checkout when everything in the cart is still available right now (checked live)
  if (isCheckingAvailability.value) return;
  const availability = await checkAvailability({
    artworkIds: cartItems.value.filter((i: any) => i.type === "artwork").map((i: any) => i.id),
    setIds: cartItems.value.filter((i: any) => i.type === "set").map((i: any) => i.id),
  });
  if (!availability.checked) {
    cartToast.add({ title: "Couldn't confirm availability", description: "Please check your connection and try again.", color: "error" });
    return;
  }
  if (!availability.ok) {
    const gone = cartItems.value
      .filter((i: any) => !(i.type === "set" ? availability.sets[String(i.id)] : availability.artworks[String(i.id)])?.available)
      .map((i: any) => i.name || "An item");
    cartToast.add({
      title: "Some items are no longer available",
      description: `${gone.slice(0, 3).join(", ")}${gone.length > 3 ? ` and ${gone.length - 3} more` : ""} — please remove ${gone.length > 1 ? "them" : "it"} from your cart to continue.`,
      color: "error",
    });
    refetchCart?.();
    return;
  }

  errors.value = {};
  const meta = user.value?.user_metadata ?? {};
  const userName = profile.value?.name || meta.full_name || meta.display_name || '';
  const userEmail = user.value?.email || '';

  let userPhone = '';
  let userCountryCode = '+91';
  let userCountryIso = 'IN';
  if (profile.value?.phone) {
    const parts = profile.value.phone.split('-');
    if (parts.length === 2 && parts[0] === '+91') {
      userCountryCode = parts[0];
      userPhone = parts[1];
      userCountryIso = 'IN';
    }
  }

  orderForm.value = {
    name: userName,
    email: userEmail,
    phone: userPhone,
    pan: "",
    address: { name: '', street: '', city: '', state: '', zip: '', country: 'India' },
    hasGst: false,
    gstName: '',
    gstNumber: '',
    countryCode: userCountryCode || '+91',
    countryIso: userCountryIso,
  };

  // Every item is asked again from scratch — the children re-report as they mount.
  framingSelections.value = {};

  closeCart();
  isCheckoutModalOpen.value = true;
};

const handleFinalPaymentTrigger = async () => {
  const f = orderForm.value;
  errors.value = {};
  
  const pendingFraming = unansweredFramingItems.value;
  if (pendingFraming.length > 0) {
    errors.value.framing = pendingFraming.length === 1
      ? `Please complete the framing option for “${pendingFraming[0]?.name}”`
      : `Please complete the framing options for ${pendingFraming.length} items`;
  }
  if (!f.name) errors.value.name = "Full name is required";
  if (!f.email) errors.value.email = "Email address is required";
  if (!f.phone) errors.value.phone = "Phone number is required";
  if (!f.address.street) errors.value.street = "Street address is required";
  if (!f.address.city) errors.value.city = "City is required";
  if (!f.address.state) errors.value.state = "State is required";
  if (!f.address.zip) {
    errors.value.zip = "Zip code is required";
  } else if (f.address.zip.length !== 6) {
    errors.value.zip = "Zip code must be exactly 6 digits";
  } else {
    const mismatch = validatePincodeWithState(f.address.zip, destinationStateName.value);
    if (!mismatch.isValid) {
      errors.value.zip = mismatch.error || "Pincode does not match selected state";
    }
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (f.email && !emailRegex.test(f.email)) errors.value.email = "Please enter a valid email address";
  if (!f.phone) errors.value.phone = "Phone number is required";
  else if (f.phone.length !== 10) errors.value.phone = "Please enter a valid 10-digit Number";

  if (f.hasGst) {
    if (!f.gstName) errors.value.gstName = "Company name is required";
    if (!f.gstNumber) errors.value.gstNumber = "GSTIN is required";
  }

  // Shipping Availability Validation
  if (isCheckingShipping.value) {
    return;
  }
  if (isInternationalPhone.value) {
    return;
  }
  if (isShippingAvailable.value === false) {
    errors.value.zip = shippingStatusMessage.value || "Shipping is currently unavailable for this pincode.";
    scrollToFirstError(errors.value);
    return;
  }

  if (Object.keys(errors.value).length > 0) {
    scrollToFirstError(errors.value);
    return;
  }

  isReserving.value = true;
  try {
    // The order stores the cart items verbatim, so each one carries its own framing answer there —
    // that is what the team frames from. customerDetails keeps the totals and the full breakdown
    // (framingOrderPayload), which is what the amount is worked out from and what gets printed.
    const cartItemsWithFraming = cartItems.value.map((item) => {
      const selection = framingSelections.value[item.cartItemId];
      if (!selection?.requiresChoice) return item;
      return {
        ...item,
        framing: selection.choice,
        framingCharges: selection.cost,
        framingRecord: selection.framingRecord,
        // The exact props this item's own live preview is drawn from — stored as-is so a later
        // read-only render (e.g. the internal Sales Summary) can bind them straight to
        // FrameVisualizer with zero reconstruction. Null when this item had nothing to preview.
        framingPreview: selection.preview,
      };
    });

    await processPayment({
      isCartCheckout: true,
      cartItems: cartItemsWithFraming,
      customerDetails: {
        name: f.name,
        email: f.email,
        phone: `${f.countryCode}${f.phone}`,
        pan: f.pan,
        hasGst: f.hasGst,
        gstName: f.hasGst ? f.gstName : "",
        gstNumber: f.hasGst ? f.gstNumber : "",
        framing: framingSummary.value,
        framingCharges: framingTotalCost.value,
        framingSpecs: framingOrderPayload.value,
        delivery_instructions: f.delivery_instructions || undefined,
        shippingCharges: totalShippingCost.value,
      },
      shippingAddress: {
        ...f.address,
        state: State.getStateByCodeAndCountry(f.address.state, "IN")?.name || f.address.state,
      },
    });

    isCheckoutModalOpen.value = false;
  } finally {
    isReserving.value = false;
  }
};

</script>

<template>
  <USlideover
    v-model:open="isCartOpen"
    title="Your Cart"
    close-icon="lucide:x"
    :ui="{
      content: 'sm:max-w-lg bg-primary text-on-primary-heading flex flex-col h-[100dvh]',
      header: 'border-b border-on-primary-border py-4 px-6 shrink-0',
      body: 'p-0 flex flex-col flex-1 min-h-0',
      title: 'text-xl font-medium tracking-wide',
    }"
  >
    <template #body>
      <div class="flex flex-col h-full w-full bg-primary">
        <!-- Empty State -->
        <div v-if="cartItems.length === 0" class="flex flex-col items-center justify-center flex-1 p-8 text-center">
          <Icon name="lucide:shopping-bag" class="w-16 h-16 text-on-primary-body/40 mb-4" />
          <h3 class="text-xl font-medium mb-2">Your cart is empty</h3>
          <p class="text-on-primary-body mb-8">Looks like you haven't added any artworks to your cart yet.</p>
          <Button variant="primary" @click="handleContinueBrowsing">Continue Browsing</Button>
        </div>

        <!-- Cart Items -->
        <div v-else class="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-6">
          <div 
            v-for="item in cartItems" 
            :key="item.cartItemId"
            class="flex p-4 border border-on-primary-border rounded-[8px] relative bg-primary group hover:border-gray-800 transition-colors cursor-pointer"
          >
            <NuxtLink 
              :to="item.type === 'set' ? `/artworks/sets/${item.id}` : `/artworks/${item.slug}`" 
              @click="closeCart"
              class="flex gap-4 flex-1 min-w-0"
            >
              <!-- Thumbnail -->
              <div class="w-24 h-24 bg-button-secondary rounded flex-shrink-0 overflow-hidden relative">
                <img 
                  v-if="item.image" 
                  :src="getImageUrl(item) || ''" 
                  :alt="item.name"
                  class="w-full h-full object-cover object-center"
                />
                <div v-else class="w-full h-full flex items-center justify-center text-on-primary-body/40">
                  <Icon name="lucide:image" class="w-6 h-6" />
                </div>
                <div v-if="item.type === 'set'" class="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold">
                  Set
                </div>
              </div>

              <!-- Details -->
              <div class="flex flex-col flex-1 min-w-0 py-1">
                <h4 class="text-base font-medium truncate pr-6">{{ item.name }}</h4>
                <p v-if="item.artist" class="text-xs text-on-primary-body uppercase tracking-wider mt-1">{{ item.artist }}</p>
                <p v-if="item.size || item.display_size" class="text-[11px] text-on-primary-body/70 mt-0.5">{{ formatDimensions(item.display_size || item.size) }}</p>
                <p class="text-sm font-medium mt-auto">
                  ₹{{ Intl.NumberFormat("en-IN").format(item.price || 0) }}
                </p>
              </div>
            </NuxtLink>

            <!-- Remove Button -->
            <button 
              class="absolute top-3 right-3 text-on-primary-body hover:text-red-500 transition-colors p-1 bg-primary border-none cursor-pointer z-10"
              aria-label="Remove item"
              @click.stop="handleRemove(item.cartItemId)"
            >
              <Icon name="lucide:trash-2" class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Footer / Checkout -->
        <div v-if="cartItems.length > 0" class="shrink-0 w-full bg-primary border-t border-on-primary-border p-4 sm:p-6 shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
          <div class="flex justify-between items-end mb-4">
            <span class="text-sm text-on-primary-body uppercase tracking-wider font-semibold">Subtotal</span>
            <span class="text-xl font-medium">₹{{ Intl.NumberFormat("en-IN").format(cartTotal) }}</span>
          </div>

          <Button class="w-full justify-center py-3 text-base" variant="primary" :loading="isCheckingAvailability" @click="proceedToCheckout">
            Proceed to Checkout
          </Button>
        </div>
      </div>
    </template>
  </USlideover>

  <!-- CHECKOUT & RESERVATION MODAL -->
  <UModal
    v-model:open="isCheckoutModalOpen"
    title="Cart Checkout"
    :dismissible="!isReserving && !anyFramingDialogOpen"
    :close="!isReserving && !anyFramingDialogOpen"
    :ui="{ content: hasFramingBuild ? 'sm:max-w-5xl transition-[max-width] duration-300 ease-out' : 'transition-[max-width] duration-300 ease-out' }"
  >
    <template #body>
      <!-- Two columns from lg up: every configured item's live preview parks on the left while the
           order details scroll on the right, as the artwork and set checkouts do with their one
           preview. Below lg this is a single column and each item draws its own preview inline. -->
      <div class="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-8">

        <!-- LEFT: one live preview per item the buyer has configured -->
        <!-- scrollbar-gutter MUST stay. Each preview sizes itself from the width it measures, and a
             taller preview is a WIDER one — so a scrollbar appearing here steals ~15px, shrinks
             every preview, drops the column back under the max height, removes the scrollbar, and
             starts again. That feedback loop reads on screen as the previews vibrating. Reserving
             the gutter permanently keeps the measured width constant either way. -->
        <div
          v-if="isPreviewBesideForm && framePreviews.length"
          class="flex flex-col gap-4 lg:flex-1 lg:min-w-0 lg:sticky lg:top-0 lg:max-h-[80vh] lg:overflow-y-auto [scrollbar-gutter:stable]"
        >
          <div v-for="preview in framePreviews" :key="preview.cartItemId" class="flex flex-col gap-1">
            <FrameVisualizer v-bind="preview.props" />
            <p class="text-[12px] text-gray-500 px-1 truncate">{{ preview.label }}</p>
          </div>
        </div>

        <!-- RIGHT: everything else -->
        <div class="flex flex-col gap-6 lg:flex-1 lg:min-w-0">
        <div class="flex flex-col gap-3">
          <div class="bg-gray-50 border-l-4 border-black p-4">
            <h4 class="font-bold text-sm mb-1 uppercase tracking-wider">Checkout</h4>
            <p class="text-xs text-gray-600 leading-relaxed">
              Complete the full payment to secure your cart items. 
              Within <strong>48 hours</strong>, our team will reach out to confirm your order, framing, and shipping details.
            </p>
          </div>

        </div>

        <!-- Artwork Framing — asked once per cart item, since each can be answered differently -->
        <div class="flex flex-col gap-4" data-error-field="framing">
          <h5 class="font-bold text-xs uppercase tracking-widest border-b pb-1">Do you want the artworks to be framed?</h5>
          <p class="text-xs text-gray-600">
            Choose how each item in your cart should arrive. Sets are configured once, on their first framable piece, and priced across every piece.
          </p>
          <!-- Once the buyer has tried to pay, the items still unanswered say so on themselves -->
          <CartItemFraming
            v-for="item in cartItems"
            :key="item.cartItemId"
            :item="item"
            :error="errors.framing ? itemFramingError(item.cartItemId) : ''"
            @update:selection="onFramingSelection"
            @update:dialogOpen="(open) => onFramingDialogOpenChange(item.cartItemId, open)"
          />
          <span v-if="errors.framing" class="text-red-500 text-xs mt-[-8px]">{{ errors.framing }}</span>
        </div>

        <!-- Personal Info -->
        <div class="flex flex-col gap-4">
          <h5 class="font-bold text-xs uppercase tracking-widest border-b pb-1">Personal Information</h5>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UFormField label="Full Name" data-error-field="name" :error="errors.name">
            <TextInput v-model="orderForm.name" placeholder="Enter your full name" :error="!!errors.name" />
          </UFormField>
          <UFormField label="Email Address" data-error-field="email" :error="errors.email">
            <TextInput v-model="orderForm.email" type="email" placeholder="your@email.com" :error="!!errors.email" />
          </UFormField>
          <UFormField label="Phone Number" data-error-field="phone" :error="errors.phone" class="w-full md:col-span-2">
            <div class="flex items-stretch gap-2 w-full">
              <CountryCodeSelect :model-value="orderForm.countryIso" @update:model-value="onPhoneCountryChange" />
              <div class="flex-1 min-w-0">
                <TextInput
                  v-model="orderForm.phone"
                  type="tel"
                  numeric-only
                  placeholder="Phone number"
                  :maxlength="isInternationalPhone ? 15 : 10"
                  :error="!!errors.phone"
                />
              </div>
            </div>
          </UFormField>
          <div v-if="isInternationalPhone" class="bg-blue-50/50 border-l-4 border-blue-400 p-4 md:col-span-2">
            <h4 class="font-bold text-sm mb-1 text-blue-800 uppercase tracking-wider">International Orders</h4>
            <p class="text-xs text-blue-700 leading-relaxed">
              We currently accept online orders for <strong>India only</strong>. For international inquiries, please reach out to us at 
              <a 
                target="_blank"
                href="https://mail.google.com/mail/?view=cm&fs=1&to=connect@quietart.com" 
                class="font-bold underline underline-offset-2"
              >connect@quietart.com</a> 
              and our team will assist you personally.
            </p>
          </div>
        </div>
        </div>

        <AddressSelector 
          v-model="orderForm.address"
          v-model:errors="errors"
        />

        <!-- Blue Dart Shipping Availability Status -->
        <ShippingStatusBadge
          :is-checking="isCheckingShipping"
          :is-available="isShippingAvailable"
          :shipping-type="shippingType"
          :message="shippingStatusMessage"
          :pincode="orderForm.address.zip"
        />

        <!-- Special Delivery Instructions -->
        <div class="flex flex-col gap-4">
          <h5 class="font-bold text-xs uppercase tracking-widest border-b pb-1">Special Instructions</h5>
          <p class="text-sm text-gray-700">Please share if you have any special instructions for smoother delivery</p>
          <textarea
            v-model="orderForm.delivery_instructions"
            rows="3"
            placeholder="e.g. Suitable times, any alternate contact number for coordination, any landmarks for easier navigation…"
            class="w-full resize-none rounded-[6px] border-0 px-4 py-3 text-sm font-medium text-on-primary-heading ring ring-inset outline-none placeholder:text-on-primary-heading/40 focus-visible:ring-2 transition-colors"
          />
        </div>

        <div class="flex flex-col gap-4 bg-gray-50 p-4 border border-gray-100">
          <label class="flex items-center gap-3 cursor-pointer group">
            <input 
              type="checkbox" 
              v-model="orderForm.hasGst"
              class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer"
            >
            <span class="text-sm text-gray-700">
              I need a GST Invoice
            </span>
          </label>
          <div v-if="orderForm.hasGst" class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <UFormField label="Name on GST Certificate" data-error-field="gstName" :error="errors.gstName">
              <TextInput v-model="orderForm.gstName" placeholder="Company Name" :error="!!errors.gstName" />
            </UFormField>
            <UFormField label="GSTIN" data-error-field="gstNumber" :error="errors.gstNumber">
              <TextInput v-model="orderForm.gstNumber" placeholder="22AAAAA0000A1Z5" class="uppercase" :error="!!errors.gstNumber" />
            </UFormField>
          </div>
        </div>

        <div class="flex flex-col gap-3 mt-2">
          <div class="flex justify-between text-sm text-gray-700">
            <span>Base Price ({{ cartItems.length }} items):</span>
            <span>₹{{ Intl.NumberFormat("en-IN").format(cartTotal) }}</span>
          </div>

          <div v-if="framingTotalCost > 0" class="flex justify-between text-sm text-gray-700">
            <span>Framing:</span>
            <span>₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(framingTotalCost) }}</span>
          </div>

          <div
            v-if="totalShippingCost > 0 || isShippingAvailable === false || pincodeMismatchError"
            class="flex justify-between text-sm text-gray-700"
          >
            <span class="flex items-center gap-1.5">
              <span>Shipping:</span>
              <span v-if="isShippingAvailable === false || pincodeMismatchError" class="text-xs text-red-500 font-medium">
                Unavailable
              </span>
              <!-- <span v-else-if="calculatedCartShipping?.totalBillableWeight" class="text-xs text-gray-500 font-normal">
                ({{ calculatedCartShipping.totalBillableWeight }} kg billable · {{ calculatedCartShipping.packageCount }} {{ calculatedCartShipping.packageCount === 1 ? 'package' : 'packages' }})
              </span> -->
              <!-- <span
                v-if="canShowShippingBreakdown && isShippingAvailable !== false && !pincodeMismatchError"
                class="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-50 group-hover:bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded transition shadow-2xs"
                title="Click to view cart calculation breakdown (Dev/Staging)"
              >
                <Icon name="lucide:calculator" class="w-3 h-3" />
                <span>Details</span>
              </span> -->
            </span>
            <span v-if="isShippingAvailable === false || pincodeMismatchError" class="text-xs font-semibold text-red-600">
              Not Available
            </span>
            <span v-else>
              ₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(totalShippingCost) }}
            </span>
          </div>

          <div class="flex justify-between text-sm text-gray-700">
            <span>GST (5%):</span>
            <span>₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(gstAmount) }}</span>
          </div>
          <div class="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-2 pb-1">
            <span>Total:</span>
            <span>₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(totalOrderAmount) }}</span>
          </div>
          <Button 
            class="w-full justify-center py-4 mt-2" 
            variant="primary" 
            :loading="isReserving"
            :disabled="isReserving || isCheckingShipping || isInternationalPhone || isShippingAvailable === false || !!pincodeMismatchError"
            @click="handleFinalPaymentTrigger"
          >
            Pay: ₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(totalOrderAmount) }}
          </Button>
        </div>
        </div>
      </div>
    </template>
  </UModal>

  <!-- SHIPPING BREAKDOWN MODAL (DEV / STAGING ONLY) -->
  <!-- <ShippingBreakdownModal
    v-if="canShowShippingBreakdown"
    v-model:open="isShippingBreakdownOpen"
    title="Cart Shipping Breakdown"
    :set-shipping="calculatedCartShipping"
    :destination-state="destinationStateName"
    :pincode="orderForm.address.zip"
  /> -->
</template>
